import { Card, Form, Flex, Select, Typography } from "antd";
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import { getAllEntityWithoutPaginationAction } from "../../../../../../../../store/action/admin/entityActions.js";
import { getAllDimensionsByEntityAction } from "../../../../../../../../store/action/admin/dimensionsEntityActions.js";
import CustomLoaderIcon from "../../../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";
import "../../../../../../../../style/dimensionSetting/dropdownStyle.css";

const { Text } = Typography;

const DimensionSettings = ({ form }) => {
  const dispatch = useDispatch();

  // Redux state
  const { info: entities } = useSelector((state) => state.getAllEntity);
  const { info: dimensions, loading: dimensionsLoading } = useSelector(
    (state) => state.getAllDimension
  );

  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedDimensions, setSelectedDimensions] = useState({});
  const [dimensionReady, setDimensionReady] = useState(false);

  // Dropdown configuration
  const dropdownConfig = [
    { label: "Trade License", name: "tradeLicense" },
    { label: "Department", name: "department" },
    { label: "Office / Branch Location", name: "branchLocation" },
    { label: "Customer", name: "client" },
  ];

  const USER_ROLE_OPTIONS = [
    { label: "Client Relation", value: "client_relation" },
    { label: "Customer User", value: "customer_user" },
    { label: "Accountant", value: "accountant" },
    { label: "Visa Typist", value: "visa_typist" },
    { label: "Medical Typist", value: "medical_typist" },
    { label: "Inside Country Typist", value: "inside_country_typist" },
    { label: "Visa Stamping Staff", value: "visa_stamping_staff" },
    { label: "Operations", value: "operations" },
    { label: "EID Typist", value: "eid_typist" },
    { label: "Cancellation / Absconder", value: "cancellation/absconder" },
  ];

  // Load all entities on mount
  useEffect(() => {
    dispatch(getAllEntityWithoutPaginationAction());
  }, [dispatch]);

  // Fetch dimensions when entity changes
  useEffect(() => {
    if (!selectedEntity) return;

    setDimensionReady(false);
    setSelectedDimensions({}); // reset previous selections

    dispatch(getAllDimensionsByEntityAction(selectedEntity)).then(() => {
      setDimensionReady(true);
    });
  }, [selectedEntity, dispatch]);

  // In edit mode, set selected entity from form
  useEffect(() => {
    const entityId = form.getFieldValue("entityId");
    if (entityId) setSelectedEntity(entityId);
  }, [form]);

  // Map form.dimensionIds into selectedDimensions when dimensions load
  useEffect(() => {
    if (!dimensionReady) return;

    const dimensionIds = form.getFieldValue("dimensionIds") || [];
    console.log({ dimensionIds });
    const grouped = {};
    dropdownConfig.forEach((dd) => {
      const options = dimensions?.[dd.name] || [];

      const optionIds = options.map((o) => String(o.dimension_id));

      grouped[dd.name] = [
        ...dimensionIds.filter((id) => optionIds.includes(String(id))),
      ];
    });

    setSelectedDimensions(grouped);
  }, [dimensionReady, dimensions, form]);

  // Handle selection changes
  const handleSelectChange = (name, values) => {
    const updated = { ...selectedDimensions, [name]: values || [] };
    setSelectedDimensions(updated);
    form.setFieldsValue({ dimensionIds: Object.values(updated).flat() });
  };

  const renderSelect = (label, name) => {
    const options = dimensions?.[name] || [];
    if (!options.length) return null;

    const optionMap = options.map((o) => ({
      label: o.name,
      value: String(o.dimension_id).trim(),
    }));

    const fieldValue =
      selectedDimensions[name]?.map((id) => String(id).trim()) || [];
    console.log({ fieldValue });
    // set form value so Select correctly marks them selected
    form.setFieldsValue({
      [name]: fieldValue,
    });

    const isCustomer = name === "client";
    const isRequired = isCustomer && options.length > 0;

    return (
      <Form.Item
        key={name}
        name={name}
        label={label}
        style={{ flex: 1, minWidth: 220, marginBottom: 8 }}
        rules={
          isRequired
            ? [{ required: true, message: "Customer selection is required" }]
            : []
        }>
        <Select
          placeholder={`Select ${label}`}
          showSearch
          mode="multiple"
          loading={dimensionsLoading}
          disabled={!selectedEntity}
          onChange={(vals) => handleSelectChange(name, vals)}
          options={optionMap}
        />
      </Form.Item>
    );
  };

  // Loader if entity selected but dimensions still loading
  const showLoader = selectedEntity && (!dimensionReady || dimensionsLoading);

  return (
    <Card
      bordered
      title={<Text strong>Dimension Settings</Text>}
      style={{
        minWidth: 338,
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        padding: 16,
      }}>
      {showLoader ? (
        <Flex
          style={{
            height: 150,
            justifyContent: "center",
            alignItems: "center",
          }}>
          <CustomLoaderIcon />
        </Flex>
      ) : (
        <Form layout="vertical" form={form}>
          {entities?.length > 0 && (
            <Flex gap={16} wrap="wrap">
              {/* Entity Dropdown */}
              <Form.Item
                name="entityId"
                label="Select Entity"
                style={{ flex: 1, minWidth: 220, marginBottom: 8 }}
                rules={[
                  { required: true, message: "Please select an entity" },
                ]}>
                <Select
                  placeholder="Select Entity"
                  showSearch
                  value={selectedEntity}
                  onChange={(value) => setSelectedEntity(value)}
                  options={entities.map((e) => ({
                    label: e.name,
                    value: e._id,
                  }))}
                />
              </Form.Item>

              {/* User Role Dropdown */}
              <Form.Item
                name="userRole"
                label="User Role"
                style={{ flex: 1, minWidth: 220, marginBottom: 8 }}
                rules={[
                  {
                    required: !!selectedEntity,
                    message: "Please select user role",
                  },
                ]}>
                <Select
                  placeholder="Select User Role"
                  showSearch
                  disabled={!selectedEntity}
                  options={USER_ROLE_OPTIONS}
                />
              </Form.Item>
            </Flex>
          )}

          <Flex gap={16} wrap="wrap">
            {dimensionReady &&
              dropdownConfig.map((dd) => renderSelect(dd.label, dd.name))}
          </Flex>

          <Form.Item name="dimensionIds" hidden>
            <input />
          </Form.Item>
        </Form>
      )}
    </Card>
  );
};

export default DimensionSettings;
